import { ReactNode } from 'react';
import { ToastsToIgnoreSettings } from 'src/environmentVariables/EnvironmentVariables';
import useNotificationStore from '../stores/useNotificationStore';
import { MarketOrderToastState } from '../components/MarketOrderToasts/MarketOrderToastStateTypes';
import { dlog } from '../dev';
import { DriftAppEventEmitter } from 'src/stores/DriftStore/useDriftStore';
import { GLOBAL_SETTINGS_CLIENT } from 'src/providers/settingsProvider';
import { SOLANA_TRANSACTION_BLOCK_HEIGHT_EXPIRY } from '../constants/constants';
import { SLOT_TIME_ESTIMATE_MS } from '@velocity-exchange/sdk';

type CoreToastData = {
	id?: string;
	hidden?: boolean;
	/**
	 * If true, will not show another toast if this is already showing.
	 * Useful if meant to be the last toast in a sequence.
	 */
	blockSubsequentToasts?: boolean;
	/**
	 * If true, will prevent the toast from being cleared by `useClearStaleNotifications`.
	 */
	isHovered?: boolean;
	/**
	 * Tracks which process last updated this notification.
	 * Used for debugging and understanding data flow.
	 */
	source?: string;
};

export interface V2AuctionToastDataBody
	extends MarketOrderToastState, CoreToastData {}

export interface MarketOrderToast extends V2AuctionToastDataBody {
	type: 'marketOrder';
}

export type CallbackAction = {
	label: string;
	callback: () => void;
	type?:
		| 'default' // label with arrow pointing right
		| 'button';
};

export type TxnLinkAction = {
	txnSig: string | string[];
	type: 'txnLink';
};

export type ExternalLinkAction = {
	label?: string;
	href: string;
	type: 'link';
};

export type CustomAction = {
	type: 'custom';
	content: ReactNode;
};

export type NotificationAction =
	| CallbackAction
	| TxnLinkAction
	| ExternalLinkAction
	| CustomAction;

// NOTE: If you add a new notification type but want it to have it's own clearing logic, make sure to adjust the list of types in useClearStaleNotifications
export type NotificationType =
	| 'error'
	| 'warning'
	| 'info'
	| 'success'
	| 'awaiting'
	| 'alert'
	| 'timeout'
	| 'awaiting_wallet_confirmation'
	| 'progress_indicator';

export interface NotificationData extends CoreToastData {
	type: NotificationType;
	bgType?: 'error' | 'info' | 'success';
	message?: ReactNode;
	description?: ReactNode;
	subDescription?: ReactNode;
	/**
	 * Allows for more than just `description` and `subDescription`.
	 */
	steps?: ReactNode[];
	action?: NotificationAction;
	offerIgnoreToast?: keyof ToastsToIgnoreSettings;
	startTimeMs: number;
	lengthMs: number;
	showUntilCancelled?: boolean;
	statusWarning?: string;
	bottomContent?: ReactNode;
	/**
	 * If `true`, will override a blocking toast.
	 */
	overrideBlockSubsequentToasts?: boolean;
	/**
	 * If `true`, shows a timeout notification if a transaction is taking too long and `TransactionErrorHandler failed to catch it.
	 */
	timeoutIfProlongedTxn?: boolean;
	/**
	 * Callback to be invoked when the notification is dismissed.
	 */
	onDismiss?: () => void;
	/**
	 *  Determines styling and behavior of "alert" notifications.
	 */
	alertType?: 'minor' | 'major';
	/**
	 * Only exists for major "alert" notifications.
	 */
	imageSrc?: string;
	/**
	 * If passed, notification will only show for the given path.
	 * Currently only implemented for "alert" notifications.
	 */
	pathToAlertOn?: string;
	/**
	 * Disables auto-dismiss of "alert" notifications.
	 * Generally should be the exception to the norm.
	 */
	disableAlertAutoDismiss?: boolean;
	/**
	 * Used to correctly order notifications in the stack. If setting this,
	 * be sure to check the `stackIndex` of other notifications as well.
	 * Currently only implemented for "alert" notifications.
	 */
	stackIndex?: number;
	/**
	 * Should only be used on timeout type notifications.
	 */
	timeoutStartMs?: number;
	/**
	 * Should only be used on timeout type notifications.
	 */
	timeoutEndMs?: number;
	/**
	 * If true, shows a white X button that works well on dark backgrounds.
	 * Useful for notifications with custom background images or dark themes.
	 */
	whiteXButton?: boolean;
}

export interface NewNotificationProps extends Omit<
	NotificationData,
	'startTimeMs' | 'lengthMs' | 'isHovered'
> {
	lengthMs?: number;
	startTimeMs?: number;
	updatePrevious?: boolean;
	showUntilCancelled?: boolean;
	onlyShowIfPreviousTarget?: boolean;
	bottomContent?: ReactNode;
}

const DEFAULT_PROPS: Partial<NewNotificationProps> = {
	updatePrevious: true,
	showUntilCancelled: false,
	onlyShowIfPreviousTarget: false,
	description: '', // this way if we updatePrevious with no description, old description will be cleared
};

/**
 * Display a notification to the user, in toast format.
 *
 * The optional `id` parameter can be used to overwrite a currently displayed notification (with the same ID) rather than display a whole new notification. You can change any of the current notification's attributes.
 *
 * The showUntilCancelled prop allows you to keep a notification on screen until you overwrite it with a later notification
 *
 * @param {NewNotificationProps} newNotificationProps
 *
 * @returns
 */
export function notify(props: NewNotificationProps) {
	const newNotificationProps = {
		...DEFAULT_PROPS,
		...props,
	};

	dlog(`notifications`, `notify`, {
		newNotificationProps,
	});

	const notifications = useNotificationStore.getState().notifications;
	const notificationStore = useNotificationStore.getState();
	const settingsClient = GLOBAL_SETTINGS_CLIENT;

	const shouldIgnoreToast =
		newNotificationProps.offerIgnoreToast &&
		settingsClient.overallSettingsState.ignoreToasts[
			newNotificationProps.offerIgnoreToast
		];

	if (shouldIgnoreToast) return;

	const newNotificationToAdd = {
		startTimeMs: Date.now(),
		...newNotificationProps,
		lengthMs: newNotificationProps.lengthMs ?? 5000,
		showUntilCancelled: newNotificationProps.showUntilCancelled,
		updatePrevious: newNotificationProps.updatePrevious,
	};

	// We let awaiting_wallet_confirmation be a special case where we basically just use the timeout notification type but will pre-fill the notification with standard content to avoid needing to repeat it everywhere
	if (props.type === 'awaiting_wallet_confirmation') {
		if (
			newNotificationProps.timeoutStartMs === undefined &&
			newNotificationProps.timeoutEndMs === undefined
		) {
			newNotificationToAdd.timeoutStartMs = Date.now();
			newNotificationToAdd.timeoutEndMs =
				Date.now() +
				SOLANA_TRANSACTION_BLOCK_HEIGHT_EXPIRY * SLOT_TIME_ESTIMATE_MS;
		}

		const autoConfirmEnabled = notificationStore.autoConfirmEnabled;
		const text = !autoConfirmEnabled
			? 'Confirm the transaction in your wallet'
			: 'Sending transaction...';

		if (text) {
			newNotificationToAdd.message = text;
		}
	}

	// Check for notifications with duplicate ID
	//// Unless updating a previous notification via its ID - then we don't want to add a duplicate for the notification
	const targetNotification = notifications.find(
		(notification) =>
			notification.id && notification.id === newNotificationProps.id
	);

	if (targetNotification) {
		// if not updating, but we have an ID clash - skip
		if (!newNotificationProps.updatePrevious) return;

		// if updating, but the previous target is already showing, skip
		if (
			targetNotification.blockSubsequentToasts &&
			!newNotificationProps.overrideBlockSubsequentToasts
		)
			return;
	}

	// if updating previous but no matching target, also skip
	if (newNotificationProps.onlyShowIfPreviousTarget && !targetNotification)
		return;

	if (targetNotification) {
		notificationStore.updateNotificationById(targetNotification.id ?? '', {
			...newNotificationToAdd,
			showUntilCancelled: newNotificationProps.showUntilCancelled,
		});
	} else {
		notificationStore.addNotification(newNotificationToAdd);
	}
}

export const getTimeoutNotificationData = (
	appEventEmitter: DriftAppEventEmitter
): NewNotificationProps => ({
	type: 'warning',
	message: 'Transaction Confirmation Timeout',
	description: `Your transaction wasn't confirmed within 30 seconds, but it may still go through. You can use the Solana explorer to check. If you experience this frequently, you should try changing your Priority Fee Settings.`,
	action: {
		label: 'Priority Fee Settings',
		callback: () =>
			appEventEmitter.emit('showModal', {
				type: 'settings',
				props: { view: 'network' },
			}),
	},
	updatePrevious: true,
	showUntilCancelled: true,
	subDescription: undefined,
});
